/**
 * SkeletonList - Prebuilt skeleton for list layouts with Framer Motion animations
 *
 * Provides a ready-to-use skeleton loader for list-based content with stagger animations.
 *
 * @example
 * <SkeletonList items={5} />
 *
 * @example
 * <SkeletonList items={10} hasAvatar hasActions animation="wave" />
 */

import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import { cn } from '../utils/classNames';

const SkeletonList = ({
  items = 5,
  hasAvatar = true,
  hasActions = false,
  className,
  animation = 'shimmer',
  duration = 1.5,
}) => {
  return (
    <div className={cn('bg-white rounded-lg shadow divide-y divide-gray-200', className)}>
      {Array.from({ length: items }, (_, index) => (
        <div
          key={index}
          className="p-4 flex items-center gap-4 animate-fade-in"
          style={{ animationDelay: `${index * 0.08}s` }}
        >
          {/* Avatar */}
          {hasAvatar && (
            <Skeleton
              variant="circle"
              width="48px"
              height="48px"
              animation={animation}
              duration={duration}
            />
          )}

          {/* Content */}
          <div className="flex-1 space-y-2">
            <Skeleton
              variant="text"
              width="60%"
              height="18px"
              animation={animation}
              duration={duration}
            />
            <Skeleton
              variant="text"
              width="40%"
              height="14px"
              animation={animation}
              duration={duration}
            />
          </div>

          {/* Actions */}
          {hasActions && (
            <div className="flex gap-2">
              <Skeleton
                variant="circle"
                width="32px"
                height="32px"
                animation={animation}
                duration={duration}
              />
              <Skeleton
                variant="circle"
                width="32px"
                height="32px"
                animation={animation}
                duration={duration}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

SkeletonList.propTypes = {
  /** Number of list items */
  items: PropTypes.number,
  /** Show avatar placeholders */
  hasAvatar: PropTypes.bool,
  /** Show action buttons */
  hasActions: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Animation type (pulse, shimmer, wave, fade) */
  animation: PropTypes.oneOf(['pulse', 'shimmer', 'wave', 'fade']),
  /** Animation duration in seconds */
  duration: PropTypes.number,
};

export default SkeletonList;
